import { useParams, Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { getProgramStatus } from "@/data/mediaPrograms";
import { useEmissionsStore } from "@/data/emissionsStore";
import { useState, useEffect } from "react";
import { ArrowLeft, Clock, Calendar, Tv, Radio, MonitorPlay, Users, PlayCircle, Share2, Heart, Bell, Youtube, Facebook, Instagram, Globe, Music2, Mic } from "lucide-react";
import { XIcon } from "@/components/icons/XIcon";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { addAlert } from "@/data/alertsStore";
import { useEventMetricsStore } from "@/data/eventMetricsStore";
import { usePresentationsStore, getPresentationV2ForEntity } from "@/data/presentationsStore";

const labels = {
  fr: { back: "Retour aux émissions", live: "Rejoindre le direct", replay: "Regarder le replay", upcoming: "Suivre l'émission", guests: "Invités :", host: "Animateur(trice) :", description: "Description :", type: "Support :", channel: "Chaîne :", notFound: "Émission introuvable" },
  ar: { back: "العودة للبرامج", live: "مشاهدة المباشر", replay: "مشاهدة الإعادة", upcoming: "متابعة البرنامج", guests: "الضيوف :", host: "تنشيط :", description: "الوصف :", type: "المنصة :", channel: "القناة :", notFound: "البرنامج غير موجود" },
  zgh: { back: "ⵓⵖⴰⵍ ⵖⵔ ⵉⵖⴰⵡⴰⵙⵏ", live: "ⵥⵕ ⴰⵙⵔⵉⴷ", replay: "ⵥⵕ ⴰⵍⵙ-ⴰⵏⵏⴰⵢ", upcoming: "ⴹⴼⵕ ⴰⵖⴰⵡⴰⵙ", guests: "ⵉⵏⴱⴳⴰⵡⵏ :", host: "ⴰⵎⵙⵡⵓⴷⴷⵓ :", description: "ⴰⴳⵍⴰⵎ :", type: "ⴰⵙⴰⵔⴰⴳ :", channel: "ⵜⴰⴱⴰⴷⵓⵜ :", notFound: "ⵓⵔ ⵉⵜⵜⵡⴰⴼⴰ ⵓⵖⴰⵡⴰⵙ" }
};

const periodicityLabels: Record<string, Record<string, string>> = {
  fr: { quotidien: "Quotidien", hebdo: "Hebdomadaire", mensuel: "Mensuel", ponctuel: "Ponctuel" },
  ar: { quotidien: "يومي", hebdo: "أسبوعي", mensuel: "شهري", ponctuel: "خاص" },
  zgh: { quotidien: "ⴰⵙⵙⴰⵏ", hebdo: "ⴰⵏⵎⵍⴰⵙⵙ", mensuel: "ⴰⵢⵢⵓⵔⴰⵏ", ponctuel: "ⵉⵥⵍⵉⵏ" }
};

const EmissionDetailPage = () => {
  const { id } = useParams();
  const { lang, dir } = useI18n();
  const l = labels[lang as keyof typeof labels] || labels.fr;
  
  const presentations = usePresentationsStore(state => state.presentations);

  const { emissions: programsInfo, fetchEmissions, isLoading } = useEmissionsStore();
  
  useEffect(() => {
    useEventMetricsStore.getState().fetchMetrics();
    if (programsInfo.length === 0) {
      fetchEmissions(true);
    }
  }, [fetchEmissions, programsInfo.length]);

  const prog = programsInfo.find(p => p.id === id);

  const { userLikes, toggleLike, trackView } = useEventMetricsStore();
  
  useEffect(() => {
    if (prog) {
      trackView(prog.id, 'emission_view');
    }
  }, [prog?.id, trackView]);

  const [hasReminder, setHasReminder] = useState(false);
  const { user } = useAuth();

  if (isLoading && programsInfo.length === 0) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center p-4">
          <h2 className="text-xl font-body">Chargement de l'émission...</h2>
        </div>
        <Footer />
      </div>
    );
  }

  if (!prog && !isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center p-4">
          <h2 className="text-2xl font-bold font-display">{l.notFound}</h2>
          <Button asChild className="mt-4"><Link to="/emissions">{l.back}</Link></Button>
        </div>
        <Footer />
      </div>
    );
  }

  const isLiked = !!userLikes[prog.id];

  const title = lang === 'fr' ? prog.frTitle : lang === 'ar' ? prog.arTitle : prog.zghTitle;
  const subject = lang === 'fr' ? prog.subject_fr : lang === 'ar' ? prog.subject_ar : prog.subject_zgh;
  const description = lang === 'fr' ? prog.description_fr : lang === 'ar' ? prog.description_ar : prog.description_zgh;
  const guests = lang === 'fr' ? prog.guests_fr : lang === 'ar' ? prog.guests_ar : prog.guests_zgh;
  const host = lang === 'fr' ? prog.host_fr : lang === 'ar' ? prog.host_ar : prog.host_zgh;
  const dateStr = lang === 'fr' ? prog.dateFormatted_fr : lang === 'ar' ? prog.dateFormatted_ar : prog.dateFormatted_zgh;
  
  const v2Data = getPresentationV2ForEntity(prog.channel, "MEDIA");
  const displayDescription = v2Data?.description || description;
  const displayMediaLogo = v2Data?.logoUrl || prog.mediaLogo;
  const displaySocials = {
    ...prog.socialLinks,
    ...(v2Data?.facebook ? { facebook: v2Data.facebook } : {}),
    ...(v2Data?.twitter ? { x: v2Data.twitter } : {}),
    ...(v2Data?.instagram ? { instagram: v2Data.instagram } : {}),
    ...(v2Data?.youtube ? { youtube: v2Data.youtube } : {}),
    ...(v2Data?.website ? { website: v2Data.website } : {}),
  };
  const hasV2Details = v2Data?.activites || v2Data?.contribution || v2Data?.projets || v2Data?.image1Url || v2Data?.image2Url;

  const status = getProgramStatus(prog.dateIso, prog.durationMinutes);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Lien copié dans le presse-papier !");
  };

  const handleReminder = () => {
    if (!user) {
      toast.error("Veuillez vous connecter pour activer le rappel.");
      return;
    }
    
    addAlert({
      title: { fr: `Rappel : ${title}`, ar: `تذكير : ${title}`, zgh: `ⴰⵙⵎⴽⵜⵉ : ${title}` },
      description: { 
        fr: `L'émission sera diffusée le ${dateStr} à ${prog.time} sur ${prog.channel}.`, 
        ar: `سيتم بث البرنامج يوم ${dateStr} على الساعة ${prog.time} على قناة ${prog.channel}.`, 
        zgh: `ⴰⵖⴰⵡⴰⵙ ⴰⴷ ⵉⵜⵜⵓⵙⴽⴰⵔ ⴰⵙⵙ ⵏ ${dateStr} ⴳ ⵜⵙⵔⴰⴳⵜ ${prog.time} ⴳ ⵜⴱⴰⴷⵓⵜ ${prog.channel}.` 
      },
      sourceName: "Kayn'in Média",
      sourceType: "Institution",
      region: "Toutes",
      theme: "Politique",
      isImportant: true,
      targetUserId: user.id,
      notificationType: "reminder_emission",
      linkUrl: window.location.href
    });
    
    setHasReminder(true);
    toast.success("Rappel activé ! Vous recevrez une notification.");
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <div className="flex-1 container py-24 max-w-5xl">
        <Button variant="ghost" className="mb-8 font-body -ml-4 hover:bg-transparent hover:text-primary transition-colors text-muted-foreground" asChild>
          <Link to="/emissions">
            <ArrowLeft className={`w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0 rtl:rotate-180`} />
            {l.back}
          </Link>
        </Button>

        <div className="bg-card rounded-3xl border border-border overflow-hidden shadow-elevated flex flex-col">
          <div className="flex flex-col md:flex-row">
            {/* Left side: Image and overlays */}
            <div className="w-full md:w-1/2 min-h-[300px] md:min-h-[400px] p-8 flex flex-col justify-end relative overflow-hidden group bg-slate-950 shrink-0 border-b md:border-b-0 md:border-r border-border/50">
              <img 
                src={(prog.posterUrl && !prog.posterUrl.startsWith('blob:')) ? prog.posterUrl : (prog.type === 'tv' ? '/images/media/tv.png' : prog.type === 'radio' ? '/images/media/radio.png' : '/images/media/web.png')} 
                alt="Background" 
                className="absolute inset-0 w-full h-full object-contain transition-transform duration-700 ease-in-out group-hover:scale-105" 
              />
              {/* Soft gradient only at top/bottom for text readability without darkening the whole image */}
              <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/60 to-transparent pointer-events-none" />
              <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
              
              <div className="absolute top-6 left-6 right-6 flex justify-between items-start">
                 <Badge variant="secondary" className="bg-white/90 backdrop-blur-md text-black font-display font-black text-sm uppercase tracking-widest px-4 py-1.5 shadow-sm">
                   {prog.type === 'tv' ? <Tv className="w-4 h-4 mr-2" /> : prog.type === 'radio' ? <Radio className="w-4 h-4 mr-2" /> : <MonitorPlay className="w-4 h-4 mr-2" />}
                   {prog.channel}
                 </Badge>

                 {status === 'live' && (
                   <Badge className="bg-red-600 text-white animate-pulse shadow-md font-bold px-4 py-1.5 border-0">
                     <span className="w-2 h-2 rounded-full bg-white mr-2" /> Live
                   </Badge>
                 )}
              </div>
              
              <h1 className="relative z-10 text-2xl md:text-3xl lg:text-4xl font-display font-black text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] leading-tight">
                {title}
              </h1>
            </div>

            {/* Right side: Description and Details */}
            <div className="w-full md:w-1/2 p-8 md:p-10 flex flex-col bg-card/50">
              <div className="flex flex-col xl:flex-row justify-between items-start gap-4 border-b border-border/50 pb-6 mb-6">
                 <div className="flex flex-wrap gap-4">
                   <div className="flex items-center text-muted-foreground font-body text-sm md:text-base">
                     <Calendar className="w-4 h-4 mr-2 text-primary/70" />
                     <span>{dateStr}</span>
                   </div>
                   <div className="flex items-center text-muted-foreground font-body text-sm md:text-base">
                     <Clock className="w-4 h-4 mr-2 text-primary/70" />
                     <span dir="ltr">{prog.time}</span>
                   </div>
                   <div className="flex items-center text-muted-foreground font-body">
                     <Badge variant="outline" className="font-body text-[10px] uppercase tracking-widest">
                       {periodicityLabels[lang]?.[prog.periodicity] || prog.periodicity}
                     </Badge>
                   </div>
                 </div>

                 {displayMediaLogo && (
                   <div className="w-16 h-16 md:w-20 md:h-20 bg-white rounded-xl shadow-sm border border-border p-2 flex items-center justify-center shrink-0">
                     <img src={displayMediaLogo} alt="Logo du média" className="w-full h-full object-contain" />
                   </div>
                 )}
              </div>

              <div className="flex gap-2 shrink-0 mb-6 flex-wrap">
                 {displaySocials?.website && (
                   <Button variant="outline" size="icon" asChild className="rounded-full w-10 h-10 border-border/60 hover:bg-muted">
                     <a href={displaySocials.website} target="_blank" rel="noopener noreferrer" title="Site Web"><Globe className="w-4 h-4 text-muted-foreground" /></a>
                   </Button>
                 )}
                 {displaySocials?.youtube && (
                   <Button variant="outline" size="icon" asChild className="rounded-full w-10 h-10 border-border/60 hover:text-red-600 hover:bg-red-50">
                     <a href={displaySocials.youtube} target="_blank" rel="noopener noreferrer" title="YouTube"><Youtube className="w-4 h-4" /></a>
                   </Button>
                 )}
                 {displaySocials?.facebook && (
                   <Button variant="outline" size="icon" asChild className="rounded-full w-10 h-10 border-border/60 hover:text-blue-600 hover:bg-blue-50">
                     <a href={displaySocials.facebook} target="_blank" rel="noopener noreferrer" title="Facebook"><Facebook className="w-4 h-4" /></a>
                   </Button>
                 )}
                 {displaySocials?.instagram && (
                   <Button variant="outline" size="icon" asChild className="rounded-full w-10 h-10 border-border/60 hover:text-pink-600 hover:bg-pink-50">
                     <a href={displaySocials.instagram} target="_blank" rel="noopener noreferrer" title="Instagram"><Instagram className="w-4 h-4" /></a>
                   </Button>
                 )}
                 {displaySocials?.tiktok && (
                   <Button variant="outline" size="icon" asChild className="rounded-full w-10 h-10 border-border/60 hover:text-black hover:bg-gray-100">
                     <a href={displaySocials.tiktok} target="_blank" rel="noopener noreferrer" title="TikTok"><Music2 className="w-4 h-4" /></a>
                   </Button>
                 )}
                 {displaySocials?.x && (
                   <Button variant="outline" size="icon" asChild className="rounded-full w-10 h-10 border-border/60 hover:text-black hover:bg-gray-100">
                     <a href={displaySocials.x} target="_blank" rel="noopener noreferrer" title="X"><XIcon className="w-4 h-4" /></a>
                   </Button>
                 )}
                 
                 <div className="flex-1"></div>

                 <Button 
                   variant="outline" 
                   size="icon"
                   onClick={handleShare}
                      title="Partager"
                      className="w-10 h-10 rounded-full border-2 border-border/60 shadow-sm transition-all duration-300 hover:scale-105 hover:border-blue-200 hover:bg-blue-50/50"
                    >
                      <Share2 className="w-4 h-4 text-muted-foreground hover:text-blue-500 transition-colors" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => {
                          const newState = !isLiked;
                          toggleLike(prog.id, 'emission');
                          if (newState) {
                            toast("Ajouté aux favoris", { icon: "❤️" });
                            if (user && user.role === 'citizen') {
                               addAlert({
                                 title: { fr: `Favoris : ${title}`, ar: `مفضلة : ${title}`, zgh: `ⵉⵙⵎⵏⴰⴷⵏ : ${title}` },
                                 description: { 
                                   fr: `Votre émission favorite sera (ou est) en ligne le ${dateStr} à ${prog.time}.`, 
                                   ar: `برنامجك المفضل سيكون (أو هو) متاحاً يوم ${dateStr} على الساعة ${prog.time}.`, 
                                   zgh: `ⴰⵖⴰⵡⴰⵙ ⵏⵏⴽ ⵉⵙⵎⵏⴰⴷⵏ ⴰⴷ ⵉⵍⵉ ⴰⵙⵙ ⵏ ${dateStr} ⴳ ⵜⵙⵔⴰⴳⵜ ${prog.time}.` 
                                 },
                                 sourceName: "Kayn'in Média",
                                 sourceType: "Institution",
                                 region: "Toutes",
                                 theme: "Politique",
                                 isImportant: false,
                                 targetUserId: user.id,
                                 notificationType: "reminder_emission",
                                 linkUrl: window.location.href
                               });
                            }
                          } else {
                            toast("Retiré des favoris", { icon: "💔" });
                          }
                      }} 
                      className={`w-10 h-10 rounded-full border-2 shadow-sm transition-all duration-300 hover:scale-105 bg-background ${
                        isLiked ? "border-pink-500 bg-pink-50 hover:bg-pink-100" : "border-border/60 hover:border-pink-200 hover:bg-pink-50/50"
                      }`}
                      title="Ajouter aux favoris"
                    >
                      <Heart className={`w-5 h-5 transition-colors duration-300 ${isLiked ? "fill-pink-500 text-pink-500" : "text-muted-foreground group-hover:text-pink-400"}`} />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={handleReminder}
                      title="Me rappeler"
                      className={`w-10 h-10 rounded-full border-2 shadow-sm transition-all duration-300 hover:scale-105 bg-background ${
                        hasReminder ? "border-amber-500 bg-amber-50 hover:bg-amber-100" : "border-border/60 hover:border-amber-200 hover:bg-amber-50/50"
                      }`}
                    >
                      <Bell className={`w-5 h-5 transition-colors duration-300 ${hasReminder ? "fill-amber-500 text-amber-500" : "text-muted-foreground hover:text-amber-400"}`} />
                    </Button>
                 </div>

              <div className="space-y-6 flex-1">
                 {subject && (
                   <div>
                     <h2 className="text-xl md:text-2xl font-display font-bold text-primary mb-2 leading-tight" dir="auto">
                       {subject}
                     </h2>
                   </div>
                 )}
                 <div>
                   <h3 className="text-xs font-body font-bold text-muted-foreground uppercase tracking-wider mb-2">{l.description}</h3>
                   <p className="text-base font-body leading-relaxed text-foreground whitespace-pre-wrap" dir="auto">{displayDescription}</p>
                 </div>

                 <div>
                   <h3 className="text-xs font-body font-bold text-muted-foreground uppercase tracking-wider mb-2">{l.guests}</h3>
                   <div className="flex items-center p-3 bg-muted/30 rounded-xl border border-border/50">
                      <Users className="w-5 h-5 mr-3 text-primary shrink-0" />
                      <span className="text-base font-body font-semibold" dir="auto">{guests}</span>
                   </div>
                 </div>

                 {host && (
                   <div>
                     <h3 className="text-xs font-body font-bold text-muted-foreground uppercase tracking-wider mb-2">{l.host}</h3>
                     <div className="flex items-center p-3 bg-muted/30 rounded-xl border border-border/50">
                        <Mic className="w-5 h-5 mr-3 text-primary shrink-0" />
                        <span className="text-base font-body font-semibold" dir="auto">{host}</span>
                     </div>
                   </div>
                 )}
              </div>

              {hasV2Details && (
                <div className="mt-8 grid gap-6 md:grid-cols-2">
                  {v2Data?.activites && (
                    <div className="bg-card p-6 rounded-2xl border border-border/50 shadow-sm md:col-span-2">
                      <h4 className="font-display font-bold text-lg mb-2">À propos du média</h4>
                      <p className="text-muted-foreground whitespace-pre-wrap" dir="auto">{v2Data.activites}</p>
                    </div>
                  )}
                  {(v2Data?.image1Url || v2Data?.image2Url) && (
                    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      {v2Data.image1Url && (
                        <div className="rounded-2xl overflow-hidden shadow-sm border border-border/50 h-64">
                          <img src={v2Data.image1Url} alt="Image représentative 1" className="w-full h-full object-cover" />
                        </div>
                      )}
                      {v2Data.image2Url && (
                        <div className="rounded-2xl overflow-hidden shadow-sm border border-border/50 h-64">
                          <img src={v2Data.image2Url} alt="Image représentative 2" className="w-full h-full object-cover" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Bottom Action Button */}
          <div className="p-8 md:p-10 bg-muted/10 border-t border-border flex justify-center">
             <div className="flex justify-center w-full">
                {status === 'live' && (
                  <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white w-full md:w-auto font-display font-bold text-lg px-12 py-6 h-auto shadow-[0_0_20px_rgba(220,38,38,0.4)] transition-all hover:scale-105">
                    <PlayCircle className="w-6 h-6 mr-3" /> {l.live}
                  </Button>
                )}
                {status === 'past' && (
                  (prog.replayUrl || prog.youtubeUrl || prog.socialLinks?.youtube || prog.socialLinks?.website) ? (
                    <Button size="lg" variant="secondary" asChild className="w-full md:w-auto font-display font-bold text-lg px-12 py-6 h-auto transition-all hover:scale-105">
                      <a href={prog.replayUrl || prog.youtubeUrl || prog.socialLinks?.youtube || prog.socialLinks?.website} target="_blank" rel="noopener noreferrer">
                        <PlayCircle className="w-6 h-6 mr-3" /> {l.replay}
                      </a>
                    </Button>
                  ) : (
                    <Button size="lg" variant="secondary" className="w-full md:w-auto font-display font-bold text-lg px-12 py-6 h-auto transition-all hover:scale-105">
                      <PlayCircle className="w-6 h-6 mr-3" /> {l.replay}
                    </Button>
                  )
                )}
                {(status === 'upcoming' || status === 'imminent') && (
                  (prog.liveUrl || prog.youtubeUrl || prog.replayUrl) ? (
                    <Button size="lg" asChild className="w-full md:w-auto font-display font-bold text-lg px-12 py-6 h-auto shadow-md transition-all hover:scale-105">
                      <a href={prog.liveUrl || prog.youtubeUrl || prog.replayUrl} target="_blank" rel="noopener noreferrer">
                         <Calendar className="w-6 h-6 mr-3" /> {l.upcoming}
                      </a>
                    </Button>
                  ) : (
                    <Button size="lg" className="w-full md:w-auto font-display font-bold text-lg px-12 py-6 h-auto shadow-md transition-all hover:scale-105">
                      <Calendar className="w-6 h-6 mr-3" /> {l.upcoming}
                    </Button>
                  )
                )}
             </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default EmissionDetailPage;
